/**
 * Typed, validated runtime configuration.
 *
 * All values originate from Vite `import.meta.env` (VITE_* variables) and are
 * therefore PUBLIC — they end up in the client bundle. Never place secrets here.
 *
 * Required configuration is validated eagerly so the application fails fast with
 * a clear message instead of sending malformed requests to the Portal.
 */

export interface AppConfig {
  /** Base URL for the Portal API. Empty string means "same origin" (dev proxy). */
  readonly apiBaseUrl: string;
  /** Brand GUID sent as the `brandID` request header. Environment-specific, not secret. */
  readonly brandId: string;
  /** Production-center group GUID used for MDenture article, delivery, and order requests. */
  readonly productionCenterId: string;
  /** Client API version sent as the `clientVersion` request header. */
  readonly clientVersion: string;
  /** Module key passed to the login endpoint (mirrors Dental.WebManager). */
  readonly moduleKey: string;
  /** Router basename / deploy sub-path. Always starts with '/' and has no trailing slash. */
  readonly basePath: string;
  /**
   * Origins of host pages allowed to exchange `postMessage` payloads with this
   * app while it is embedded in an iframe. Empty means "postMessage disabled".
   */
  readonly allowedParentOrigins: readonly string[];
}

const GUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const DEFAULT_PRODUCTION_CENTER_ID = '1F9924E3-F3C1-4D0E-BBA7-8EDBE920EA0A';

export class ConfigError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'ConfigError';
  }
}

function normalizeBaseUrl(raw: string | undefined): string {
  const value = (raw ?? '').trim();
  if (value === '') {
    return '';
  }
  // Strip a single trailing slash so callers can safely concatenate `/SuiteAPI/...`.
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/** Normalises the deploy sub-path to a router basename ('' or '/sub/path'). */
function normalizeBasePath(raw: string | undefined): string {
  const value = (raw ?? '/').trim();
  if (value === '' || value === '/') {
    return '/';
  }
  const withLeading = value.startsWith('/') ? value : `/${value}`;
  return withLeading.endsWith('/') ? withLeading.slice(0, -1) : withLeading;
}

/**
 * Parses the comma-separated parent-origin allowlist.
 *
 * SECURITY: entries must be bare origins (scheme + host + optional port). A path,
 * a wildcard or a malformed URL is rejected at startup rather than silently
 * widening who may talk to the embedded app.
 */
function parseAllowedOrigins(raw: string | undefined): readonly string[] {
  const value = (raw ?? '').trim();
  if (value === '') {
    return [];
  }

  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '')
    .map((entry) => {
      if (entry === '*') {
        throw new ConfigError(
          'Invalid "VITE_ALLOWED_PARENT_ORIGINS": "*" is not permitted — list explicit origins.',
        );
      }
      let url: URL;
      try {
        url = new URL(entry);
      } catch {
        throw new ConfigError(
          `Invalid "VITE_ALLOWED_PARENT_ORIGINS" entry "${entry}": expected an origin such as https://example.com.`,
        );
      }
      if (url.origin !== entry.replace(/\/$/, '')) {
        throw new ConfigError(
          `Invalid "VITE_ALLOWED_PARENT_ORIGINS" entry "${entry}": expected a bare origin without path or query.`,
        );
      }
      return url.origin;
    });
}

/**
 * Reads and validates configuration from the provided environment record.
 * Kept pure (env is injected) so it is trivially unit-testable.
 *
 * @throws {ConfigError} when required, non-secret configuration is missing or malformed.
 */
export function readConfig(env: ImportMetaEnv): AppConfig {
  const brandId = (env.VITE_PORTAL_BRAND_ID ?? '').trim();

  if (brandId === '') {
    throw new ConfigError(
      'Missing required configuration "VITE_PORTAL_BRAND_ID". ' +
        'Copy MDenture/.env.example to .env.local and set the brand GUID for your environment.',
    );
  }

  if (!GUID_PATTERN.test(brandId)) {
    throw new ConfigError(`Invalid "VITE_PORTAL_BRAND_ID": expected a GUID but received "${brandId}".`);
  }

  const configuredProductionCenterId = (env.VITE_MDENTURE_PRODUCTION_CENTER_ID ?? '').trim();
  const productionCenterId = configuredProductionCenterId || DEFAULT_PRODUCTION_CENTER_ID;
  if (!GUID_PATTERN.test(productionCenterId)) {
    throw new ConfigError(
      `Invalid "VITE_MDENTURE_PRODUCTION_CENTER_ID": expected a GUID but received "${productionCenterId}".`,
    );
  }

  return {
    apiBaseUrl: normalizeBaseUrl(env.VITE_PORTAL_API_BASE_URL),
    brandId,
    productionCenterId,
    clientVersion: (env.VITE_PORTAL_CLIENT_VERSION ?? '1.0.2020.5220').trim(),
    moduleKey: (env.VITE_PORTAL_MODULE_KEY ?? 'MGR').trim(),
    basePath: normalizeBasePath(env.VITE_BASE_PATH),
    allowedParentOrigins: parseAllowedOrigins(env.VITE_ALLOWED_PARENT_ORIGINS),
  };
}

let cached: AppConfig | null = null;

/** Returns the validated application configuration (memoised). */
export function getConfig(): AppConfig {
  if (cached === null) {
    cached = readConfig(import.meta.env);
  }
  return cached;
}
